import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import Badge from "@cloudscape-design/components/badge";
import ButtonDropdown from "@cloudscape-design/components/button-dropdown";
import TextFilter from "@cloudscape-design/components/text-filter";
import Tabs from "@cloudscape-design/components/tabs";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { notificationsStore } from "../stores/notifications";
import { notificationsAddModalStore } from "../stores/notificationsAddModal";
import { notificationsEditModalStore } from "../stores/notificationsEditModal";
import { notificationsStatsModalStore } from "../stores/notificationsStatsModal";
import { notificationsRepeatModalStore } from "../stores/notificationsRepeatModal";
import { notificationTagsStore } from "../stores/notificationTags";
import Select from "@cloudscape-design/components/select";
import Grid from "@cloudscape-design/components/grid";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import { FormField, SpaceBetween } from "@cloudscape-design/components";

class Notifications extends Component {
	constructor(props) {
		super(props);
		this.state = {
			activeTabId: "notifications",
			searchType: { value: "id", label: "id" },
			search: "",
			queueSearch: "",
			queueDateRange: null,
		};
	}

	componentDidMount() {
		this.props.Interface.loadNotifications(this.state.searchType, this.state.search);
		this.props.Interface.loadNotificationTags().catch(() => {});
	}

	handleNotificationsSearch = () => {
		const { searchType, search } = this.state;
		const { notifications, setNotifications } = this.props;

		setNotifications({
			...notifications,
			pagination: {
				...notifications.pagination,
				current: 1,
			},
		});

		this.props.Interface.loadNotifications(searchType, search, 1);
	};

	handleQueueSearch = () => {
		const { queueSearch, queueDateRange } = this.state;
		const { notifications, setNotifications } = this.props;
		const queue = notifications.queue && typeof notifications.queue === "object" ? notifications.queue : {};

		setNotifications({
			...notifications,
			queue: {
				...queue,
				pagination: {
					...(queue.pagination || {}),
					current: 1,
				},
			},
		});

		this.props.Interface.loadNotificationQueue(queueSearch, 1, queueDateRange);
	};

	handleClearQueueDateRange = () => {
		const { notifications, setNotifications } = this.props;
		const queue = notifications.queue && typeof notifications.queue === "object" ? notifications.queue : {};

		this.setState({ queueDateRange: null });
		setNotifications({
			...notifications,
			queue: {
				...queue,
				currentDateRange: null,
				pagination: {
					...(queue.pagination || {}),
					current: 1,
				},
			},
		});

		setTimeout(() => {
			this.props.Interface.loadNotificationQueue(this.state.queueSearch, 1, null);
		}, 0);
	};

	handleQueueDateRangeChange = (value) => {
		if (!value) {
			this.handleClearQueueDateRange();
			return;
		}

		this.setState({ queueDateRange: value });
	};

	handleEditNotification = (item) => {
		this.openNotificationEditModal(item);
	};

	openNotificationEditModal = (item) => {
		const { setNotificationsEditModal, notificationsEditModal } = this.props;
		const parseJsonValue = (value, fallback) => {
			if (!value) return fallback;
			if (typeof value !== "string") return value;

			try {
				return JSON.parse(value);
			} catch (error) {
				return fallback;
			}
		};
		const showSettings = parseJsonValue(item.showSettingsConditions, []);
		const showSettingsObject = Array.isArray(showSettings) ? showSettings[0] : showSettings;
		const cleanShowSettings = { ...(showSettingsObject || {}) };
		delete cleanShowSettings.tags;
		const notificationTagIds = parseJsonValue(item.notificationTagIds, []);
		const conditions = parseJsonValue(item.cohortConditions, []);

		setNotificationsEditModal({
			...notificationsEditModal,
			show: true,
			editId: item.id,
			formData: {
				...item,
				showSettings: Object.keys(cleanShowSettings).length ? [cleanShowSettings] : [],
				tags: Array.isArray(notificationTagIds) ? notificationTagIds.map(Number) : [],
				conditions: Array.isArray(conditions) ? conditions : [],
			},
		});
	};

	handleToggleStatus = async (item) => {
		const currentStatus = item.active === true || item.active === 1 || item.active === "1";
		const newStatus = !currentStatus;

		try {
			item = {
				...item,
				showSettings: item.showSettingsConditions ? [JSON.parse(item.showSettingsConditions || "[]")] : [],
			};

			if (item.showSettings.length > 0 && item.showSettings[0].page) item.page = item.showSettings[0].page;
			if (item.showSettings.length > 0 && item.showSettings[0].seconds !== undefined) item.seconds = item.showSettings[0].seconds;

			await this.props.Interface.request(
				"set",
				{
					class: "data",
					module: "notification",
					method: "update",
				},
				{
					...item,
					cohort: item.cohortConditions ? JSON.parse(item.cohortConditions || "[]") : [],
					id: item.id,
					active: newStatus ? 1 : 0,
				},
			);

			this.props.Interface.loadNotifications(this.state.searchType, this.state.search);
			this.props.Interface.addNoty("Success", `Notification ${newStatus ? "activated" : "paused"}`);
		} catch (error) {
			console.error("Error toggling status:", error);
			this.props.Interface.addNoty("Error", error.text || "Failed to change notification status");
		}
	};

	handleShowStats = (item) => {
		const { setNotificationsStatsModal } = this.props;
		setNotificationsStatsModal((prev) => ({
			...prev,
			show: true,
			data: item,
		}));
	};

	parseNotificationJson = (value, fallback = []) => {
		if (!value) return fallback;
		if (typeof value !== "string") return value;
		try {
			return JSON.parse(value);
		} catch (error) {
			return fallback;
		}
	};

	isNotificationRepeatActive = (item) => {
		const active = item.active === true || item.active === 1 || item.active === "1";
		const supportedType = ["default", "system"].includes(String(item.type || "default"));
		const now = Date.now();
		const from = item.from ? new Date(item.from).getTime() : Number.NEGATIVE_INFINITY;
		const to = item.to ? new Date(item.to).getTime() : Number.POSITIVE_INFINITY;
		return supportedType && active && !Number.isNaN(from) && !Number.isNaN(to) && from <= now && to >= now;
	};

	handleRepeatNotification = (item) => {
		if (!this.isNotificationRepeatActive(item)) {
			this.props.Interface.addNoty("Error", "Only active notifications can be repeated");
			return;
		}

		const cohortConditions = this.parseNotificationJson(item.cohortConditions, []);
		this.props.setNotificationsRepeatModal((prev) => ({
			...prev,
			show: true,
			loaded: true,
			data: item,
			cohortConditions: Array.isArray(cohortConditions) ? cohortConditions : [],
			audiencePreview: {
				loading: false,
				count: null,
				sentUsers: null,
				expiredUsers: null,
				readUsers: null,
				queuedUsers: null,
				error: "",
			},
		}));
	};

	handleDuplicateNotification = async (item) => {
		const { setNotificationsAddModal, notificationsAddModal, setNotificationsEditModal, notificationsEditModal } = this.props;

		try {
			const cloneData = cloneDeep(item);

			delete cloneData.id;
			delete cloneData.createdAt;
			delete cloneData.updatedAt;

			cloneData.localName = (cloneData.localName || "") + "_copy";
			cloneData.active = false;
			cloneData.imageFile = null;
			if (cloneData.showSettingsConditions) {
				const showSettings = JSON.parse(cloneData.showSettingsConditions);
				delete showSettings.tags;
				cloneData.showSettings = [showSettings];
			} else {
				cloneData.showSettings = [];
			}
			cloneData.tags = this.parseNotificationJson(cloneData.notificationTagIds, []).map(Number);

			if (cloneData.cohortConditions) {
				cloneData.conditions = JSON.parse(cloneData.cohortConditions);
			} else {
				cloneData.conditions = [];
			}

			if (cloneData.showSettings && !Array.isArray(cloneData.showSettings)) {
				if (Object.keys(cloneData.showSettings).length > 0 && cloneData.showSettings.page) {
					cloneData.showSettings = [cloneData.showSettings];
				} else {
					cloneData.showSettings = [];
				}
			} else if (!cloneData.showSettings) {
				cloneData.showSettings = [];
			}

			setNotificationsEditModal({
				...notificationsEditModal,
				show: false,
				editId: null,
				formData: {},
			});

			setNotificationsAddModal({
				...notificationsAddModal,
				show: true,
				formData: cloneData,
			});

			this.props.Interface.addNoty("Info", "Notification data copied. Please review and save to create a new notification.");
		} catch (error) {
			console.error("Error duplicating notification:", error);
			this.props.Interface.addNoty("Error", "Failed to duplicate notification");
		}
	};

	formatDateTime = (dateString) => {
		if (!dateString) return "-";
		return new Date(dateString).toLocaleString();
	};

	formatNotificationTags = (item) => {
		let keys = [];
		try {
			const tagIds = typeof item.notificationTagIds === "string" ? JSON.parse(item.notificationTagIds) : item.notificationTagIds;
			keys = Array.isArray(tagIds) ? tagIds.map(Number) : [];
		} catch (error) {
			keys = [];
		}

		if (!keys.length) return "-";
		return keys.map((id) => this.props.notificationTags.data.find((tag) => Number(tag.id) === id)?.name || `#${id}`).join(", ");
	};

	getNotificationPerPageOptions = () => {
		const options = this.props.config.notifications?.preferences?.perPage;
		if (Array.isArray(options)) {
			return options;
		}

		return [
			{ value: 10, label: "10" },
			{ value: 25, label: "25" },
			{ value: 50, label: "50" },
			{ value: 100, label: "100" },
		];
	};

	getDateRangePickerI18nStrings = () => ({
		todayAriaLabel: "Today",
		nextMonthAriaLabel: "Next month",
		previousMonthAriaLabel: "Previous month",
		customRelativeRangeDurationLabel: "Duration",
		customRelativeRangeDurationPlaceholder: "Enter duration",
		customRelativeRangeOptionLabel: "Custom range",
		customRelativeRangeOptionDescription: "Define a custom range",
		customRelativeRangeUnitLabel: "Unit of time",
		formatRelativeRange: ({ startDate, endDate }) => {
			const start = new Date(startDate).toLocaleString("en-GB");
			const end = new Date(endDate).toLocaleString("en-GB");
			return `${start} - ${end}`;
		},
		formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
		dateTimeConstraintText: "Date and time must be within range.",
		renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
		labelRelativeRange: "Relative range",
		labelAbsoluteRange: "Absolute range",
		dateTimePickerAriaLabel: "Date and time picker",
		dateTimePickerLabel: "Date and Time",
		relativeModeTitle: "Relative mode",
		absoluteModeTitle: "Absolute mode",
		cancelButtonLabel: "Cancel",
		applyButtonLabel: "Apply",
		clearButtonAriaLabel: "Clear date range",
		clearButtonLabel: "Clear date range",
	});

	isValidDateRange = (range) => {
		if (!range) {
			return { valid: true };
		}

		if (range.type !== "absolute") {
			return { valid: false };
		}

		const [startDateWithoutTime] = String(range.startDate || "").split("T");
		const [endDateWithoutTime] = String(range.endDate || "").split("T");

		if (!startDateWithoutTime || !endDateWithoutTime) {
			return {
				valid: false,
				errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
			};
		}

		if (new Date(range.startDate) - new Date(range.endDate) > 0) {
			return {
				valid: false,
				errorMessage: "The selected date range is invalid. The start date must be before the end date.",
			};
		}

		return { valid: true };
	};

	renderError(errorState, dismissHandler) {
		if (!errorState?.text?.length) {
			return null;
		}

		return (
			<Box margin={{ bottom: "m" }}>
				<Alert type="error" header="Error loading data" dismissible onDismiss={dismissHandler}>
					<Box variant="p">{errorState.text}</Box>
					<hr />
					<Box variant="p">
						Request error code: {errorState.code} | Request timestamp: {errorState.timestamp}
					</Box>
				</Alert>
			</Box>
		);
	}

	renderNotificationsTable() {
		const { notifications, setNotifications, config, setConfig, setNotificationsAddModal, notificationsAddModal } = this.props;
		const perPageOptions = this.getNotificationPerPageOptions();

		return (
			<>
				{this.renderError(notifications.error, () => setNotifications({ ...notifications, error: { text: "", code: 0, timestamp: 0 } }))}

				<Table
					stickyHeader
					trackBy="id"
					loading={!notifications.loaded}
					sortingColumn={{ sortingField: notifications.sorting.sortingColumn.column }}
					sortingDescending={notifications.sorting.isDescending}
					onSortingChange={(sorting) => {
						setNotifications({
							...notifications,
							sorting: {
								...notifications.sorting,
								sortingColumn: {
									order: !sorting.detail.isDescending ? "ASC" : "DESC",
									column: sorting.detail.sortingColumn.sortingField,
								},
								isDescending: sorting.detail.isDescending,
							},
						});
						setTimeout(() => {
							this.props.Interface.loadNotifications(this.state.searchType, this.state.search);
						}, 250);
					}}
					columnDefinitions={[
						{
							id: "id",
							header: "ID",
							sortingField: "id",
							width: 80,
							cell: (item) => (
								<Button variant="link" onClick={() => this.handleEditNotification(item)}>
									<span style={{ whiteSpace: "nowrap" }}>#{item.id}</span>
								</Button>
							),
						},
						{
							id: "localName",
							header: "Local Name",
							sortingField: "localName",
							cell: (item) => item.localName || "-",
						},
						{
							id: "type",
							header: "Type",
							sortingField: "localName",
							cell: (item) => item.type || "-",
						},
						{
							id: "tags",
							header: "Tags",
							maxWidth: 300,
							cell: (item) => this.formatNotificationTags(item),
						},
						{
							id: "priority",
							header: "Priority",
							sortingField: "priority",
							width: 100,
							cell: (item) => {
								const priority = item.priority || 1;
								let color = "grey";
								if (priority >= 5) {
									color = "green";
								} else if (priority >= 3) {
									color = "blue";
								}

								return <Badge color={color}>{priority}</Badge>;
							},
						},
						{
							id: "active",
							header: "Status",
							sortingField: "active",
							cell: (item) => <Badge color={item.active ? "green" : "grey"}>{item.active ? "Active" : "Inactive"}</Badge>,
						},
						{
							id: "createdAt",
							header: "Created At",
							sortingField: "createdAt",
							cell: (item) => this.formatDateTime(item.createdAt),
						},
						{
							id: "updatedAt",
							header: "Updated At",
							sortingField: "updatedAt",
							cell: (item) => this.formatDateTime(item.updatedAt),
						},
						{
							id: "actions",
							header: "Actions",
							cell: (item) => (
								<div
									style={{
										textAlign: "center",
										whiteSpace: "nowrap",
										minWidth: "150px",
										width: "150px",
										overflow: "hidden",
									}}>
									<ButtonDropdown
										items={[
											{ text: "Edit", id: "edit" },
											{ text: item.active ? "Pause" : "Activate", id: "toggle" },
											{ text: "Statistics", id: "stats" },
											{ text: "Repeat", id: "repeat", disabled: !this.isNotificationRepeatActive(item) },
											{ text: "Duplicate", id: "duplicate" },
										]}
										onItemClick={(e) => {
											switch (e.detail.id) {
												case "edit":
													this.handleEditNotification(item);
													break;
												case "toggle":
													this.handleToggleStatus(item);
													break;
												case "stats":
													this.handleShowStats(item);
													break;
												case "repeat":
													this.handleRepeatNotification(item);
													break;
												case "duplicate":
													this.handleDuplicateNotification(item);
													break;
													this.handleDuplicateNotification(item);
													break;
												default:
													break;
											}
										}}
										expandToViewport>
										Actions
									</ButtonDropdown>
								</div>
							),
							minWidth: 150,
						},
					]}
					items={notifications.data}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{
								title: "Notifications per page",
								options: perPageOptions,
							}}
							preferences={{ pageSize: config.notifications?.perPage || 25 }}
							title="Preferences"
							onConfirm={(preferences) => {
								localStorage.setItem("notifications.perPage", preferences.detail.pageSize);
								setConfig({
									...config,
									notifications: {
										...config.notifications,
										perPage: preferences.detail.pageSize,
									},
								});
								setTimeout(() => {
									if (this.state.activeTabId === "queue") {
										this.props.Interface.loadNotificationQueue(this.state.queueSearch);
									} else {
										this.props.Interface.loadNotifications(this.state.searchType, this.state.search);
									}
								}, 250);
							}}
						/>
					}
					pagination={
						<Pagination
							disabled={!notifications.loaded}
							currentPageIndex={notifications.pagination.current}
							pagesCount={notifications.pagination.total}
							onChange={(page) => {
								setNotifications({
									...notifications,
									pagination: {
										...notifications.pagination,
										current: page.detail.currentPageIndex,
									},
								});
								setTimeout(() => {
									this.props.Interface.loadNotifications(this.state.searchType, this.state.search);
								}, 250);
							}}
						/>
					}
					filter={
						<Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
							<Select
								filteringType="auto"
								placeholder="search type"
								selectedOption={this.state.searchType}
								onChange={(option) => {
									this.setState({ searchType: option.detail.selectedOption, search: "" });
								}}
								options={[
									{ value: "id", label: "ID" },
									{ value: "localname", label: "Local name" },
									{ value: "date", label: "Date" },
									{ value: "type", label: "Type" },
								]}
							/>
							{this.state.searchType.value === "date" ? (
								<>
									<Select
										filteringType="auto"
										placeholder="type"
										selectedOption={this.state.search.type ? { value: this.state.search.type, label: this.state.search.type } : null}
										onChange={(option) => {
											this.setState({
												search: { ...(this.state.search ? this.state.search : {}), type: option.detail.selectedOption.value },
											});
										}}
										options={[
											{ value: "default", label: "default" },
											{ value: "system", label: "system" },
										]}
									/>
									<DateRangePicker
										onChange={({ detail }) => {
											this.setState({
												search: {
													...(this.state.search ? this.state.search : {}),
													from: detail.value.startDate.split("+")[0],
													to: detail.value.endDate.split("+")[0],
												},
											});
										}}
										value={{
											startDate: this.state.search?.from,
											endDate: this.state.search?.to,
											type: "absolute",
										}}
										relativeOptions={[]}
										isValidRange={(range) => {
											if (range.type === "absolute") {
												const [startDateWithoutTime] = range.startDate.split("T");
												const [endDateWithoutTime] = range.endDate.split("T");
												if (!startDateWithoutTime || !endDateWithoutTime) {
													return {
														valid: false,
														errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
													};
												}
												if (new Date(range.startDate) - new Date(range.endDate) > 0) {
													return {
														valid: false,
														errorMessage: "The selected date range is invalid. The start date must be before the end date.",
													};
												}
											} else {
												return { valid: false };
											}
										}}
										i18nStrings={{
											todayAriaLabel: "Today",
											nextMonthAriaLabel: "Next month",
											previousMonthAriaLabel: "Previous month",
											customRelativeRangeDurationLabel: "Duration",
											customRelativeRangeDurationPlaceholder: "Enter duration",
											customRelativeRangeOptionLabel: "Custom range",
											customRelativeRangeOptionDescription: "Define a custom range in the past",
											customRelativeRangeUnitLabel: "Unit of time",
											formatRelativeRange: ({ startDate, endDate }) => {
												const start = new Date(startDate);
												const end = new Date(endDate);
												const locale = "en-GB";
												const dateOptions = { year: "numeric", month: "long", day: "numeric" };
												const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };
												const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
												const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);
												return `${formattedStart} - ${formattedEnd}`;
											},
											formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
											dateTimeConstraintText: "Date and time must be within the selected range.",
											renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
											labelRelativeRange: "Relative range",
											labelAbsoluteRange: "Absolute range",
											dateTimePickerAriaLabel: "Date and time picker",
											dateTimePickerLabel: "Date and Time",
											clearButtonAriaLabel: "Clear date range",
											relativeModeTitle: "Relative mode",
											absoluteModeTitle: "Absolute mode",
											cancelButtonLabel: "Cancel",
											applyButtonLabel: "Apply",
										}}
										placeholder="From/to"
									/>
								</>
							) : this.state.searchType.value === "type" ? (
								<Select
									filteringType="auto"
									placeholder="type"
									selectedOption={this.state.search ? { value: this.state.search, label: this.state.search } : null}
									onChange={(option) => {
										this.setState({ search: option.detail.selectedOption.value });
									}}
									options={[
										{ value: "default", label: "default" },
										{ value: "system", label: "system" },
									]}
								/>
							) : (
								<TextFilter filteringText={this.state.search} onChange={({ detail }) => this.setState({ search: detail.filteringText })} filteringPlaceholder="Search by ID, name, priority, status..." filteringAriaLabel="Filter notifications" />
							)}
							<Button onClick={this.handleNotificationsSearch}>Search</Button>
						</Grid>
					}
					header={
						<Header
							variant="h1"
							description="Manage notification instances for your platform"
							counter={`(${notifications.pagination.count})`}
							actions={
								<Button
									iconName="add-plus"
									disabled={!notifications.loaded}
									variant="primary"
									onClick={() => {
										setNotificationsAddModal({ ...notificationsAddModal, show: true });
									}}>
									Add Notification
								</Button>
							}>
							Notifications
						</Header>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No notifications found</b>
							<Box padding={{ bottom: "s" }} variant="p" color="inherit">
								No notifications match the current criteria.
							</Box>
							<Button onClick={() => setNotificationsAddModal({ ...notificationsAddModal, show: true })}>Add Notification</Button>
						</Box>
					}
				/>
			</>
		);
	}

	openNotificationTagModal = (mode, tag = null) => {
		this.props.setNotificationTags((prev) => ({
			...prev,
			show: true,
			mode,
			editingTag: mode === "edit" ? tag : null,
		}));
	};

	renderNotificationTagsTable() {
		const { notificationTags, setNotificationTags } = this.props;

		return (
			<>
				{notificationTags.error && (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading notification tags" dismissible onDismiss={() => setNotificationTags((prev) => ({ ...prev, error: "" }))}>
							{notificationTags.error}
						</Alert>
					</Box>
				)}
				<Table
					stickyHeader
					trackBy="id"
					loading={!notificationTags.loaded}
					items={notificationTags.data}
					columnDefinitions={[
						{ id: "id", header: "ID", width: 80, cell: (item) => item.id },
						{ id: "key", header: "Key", cell: (item) => item.key },
						{ id: "type", header: "Type", width: 110, cell: (item) => (item.system ? <Badge color="blue">System</Badge> : "Custom") },
						{ id: "name", header: "Name", cell: (item) => item.name },
						{ id: "nameType", header: "Name type", cell: (item) => item.nameType },
						{ id: "sortOrder", header: "Order", width: 100, cell: (item) => item.sortOrder },
						{
							id: "active",
							header: "Status",
							width: 120,
							cell: (item) => <Badge color={Number(item.active) === 1 ? "green" : "grey"}>{Number(item.active) === 1 ? "Active" : "Inactive"}</Badge>,
						},
						{
							id: "edit",
							header: "Edit",
							minWidth: 150,
							cell: (item) => <Button onClick={() => this.openNotificationTagModal("edit", item)}>Edit</Button>,
						},
					]}
					header={
						<Header
							variant="h1"
							description="Create and edit notification tags and their website tab order"
							counter={`(${notificationTags.data.length})`}
							actions={
								<Button iconName="add-plus" variant="primary" onClick={() => this.openNotificationTagModal("create")}>
									Create tag
								</Button>
							}>
							Notification tags
						</Header>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No notification tags configured</b>
							<Box padding={{ bottom: "s" }} variant="p" color="inherit">
								Create the first tag to make it available for notifications.
							</Box>
							<Button onClick={() => this.openNotificationTagModal("create")}>Create tag</Button>
						</Box>
					}
				/>
			</>
		);
	}

	renderQueueTable() {
		const { notifications, setNotifications, config, setConfig } = this.props;
		const queue = notifications.queue && typeof notifications.queue === "object" ? notifications.queue : {};
		const queueItems = Array.isArray(queue.data) ? queue.data : [];
		const queueError = queue.error && typeof queue.error === "object" ? queue.error : { text: "", code: 0, timestamp: 0 };
		const queueSorting = queue.sorting && typeof queue.sorting === "object" ? queue.sorting : { sortingColumn: { order: "DESC", column: "sendDate" }, isDescending: true };
		const queuePagination = queue.pagination && typeof queue.pagination === "object" ? queue.pagination : { current: 1, total: 0, count: 0 };
		const perPageOptions = this.getNotificationPerPageOptions();

		return (
			<>
				{this.renderError(queueError, () =>
					setNotifications({
						...notifications,
						queue: {
							...queue,
							error: { text: "", code: 0, timestamp: 0 },
						},
					}),
				)}

				<Table
					stickyHeader
					trackBy="id"
					loading={!queue.loaded}
					sortingColumn={{ sortingField: queueSorting.sortingColumn.column }}
					sortingDescending={queueSorting.isDescending}
					onSortingChange={(sorting) => {
						setNotifications({
							...notifications,
							queue: {
								...queue,
								sorting: {
									...queueSorting,
									sortingColumn: {
										order: !sorting.detail.isDescending ? "ASC" : "DESC",
										column: sorting.detail.sortingColumn.sortingField,
									},
									isDescending: sorting.detail.isDescending,
								},
								pagination: {
									...queuePagination,
									current: 1,
								},
							},
						});
						setTimeout(() => {
							this.props.Interface.loadNotificationQueue(this.state.queueSearch, 1, this.state.queueDateRange);
						}, 250);
					}}
					columnDefinitions={[
						{
							id: "notificationId",
							header: "ID",
							sortingField: "notificationId",
							width: 100,
							cell: (item) => (item.notificationId ? `${item.notificationId}` : "-"),
						},
						{
							id: "uid",
							header: "UID",
							sortingField: "uid",
							cell: (item) => item.uid || "-",
						},
						{
							id: "localName",
							header: "Local Name",
							sortingField: "localName",
							cell: (item) => item.localName || "-",
						},
						{
							id: "status",
							header: "Status",
							sortingField: "status",
							cell: (item) => <Badge color="blue">{item.status || "Queue"}</Badge>,
						},
						{
							id: "sendDate",
							header: "Start Date",
							sortingField: "sendDate",
							cell: (item) => this.formatDateTime(item.sendDate),
						},
					]}
					items={queueItems}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{
								title: "Notifications per page",
								options: perPageOptions,
							}}
							preferences={{ pageSize: config.notifications?.perPage || 25 }}
							title="Preferences"
							onConfirm={(preferences) => {
								localStorage.setItem("notifications.perPage", preferences.detail.pageSize);
								setConfig({
									...config,
									notifications: {
										...config.notifications,
										perPage: preferences.detail.pageSize,
									},
								});
								setTimeout(() => {
									this.props.Interface.loadNotificationQueue(this.state.queueSearch, 1, this.state.queueDateRange);
								}, 250);
							}}
						/>
					}
					pagination={
						queueItems.length > 0 && queuePagination.total > 1 ? (
							<Pagination
								disabled={!queue.loaded}
								currentPageIndex={queuePagination.current}
								pagesCount={queuePagination.total}
								onChange={(page) => {
									setNotifications({
										...notifications,
										queue: {
											...queue,
											pagination: {
												...queuePagination,
												current: page.detail.currentPageIndex,
											},
										},
									});
									setTimeout(() => {
										this.props.Interface.loadNotificationQueue(this.state.queueSearch, page.detail.currentPageIndex, this.state.queueDateRange);
									}, 250);
								}}
							/>
						) : null
					}
					filter={
						<SpaceBetween direction="horizontal" size="xs">
							<div style={{ width: "400px" }}>
								<TextFilter filteringText={this.state.queueSearch} onChange={({ detail }) => this.setState({ queueSearch: detail.filteringText })} filteringPlaceholder="Search by ID, local name, UID, status..." filteringAriaLabel="Filter queued notifications" />
							</div>
							<DateRangePicker value={this.state.queueDateRange} onChange={({ detail }) => this.handleQueueDateRangeChange(detail.value)} i18nStrings={this.getDateRangePickerI18nStrings()} dateOnly isValidRange={this.isValidDateRange} placeholder="Start date" relativeOptions={[]} />
							<Button onClick={this.handleQueueSearch}>Search</Button>
						</SpaceBetween>
					}
					header={
						<Header variant="h1" description="Queued messages pending delivery to players" counter={`(${queueItems.length > 0 ? queuePagination.count : 0})`}>
							Message Queue
						</Header>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No queued messages found</b>
							<Box padding={{ bottom: "s" }} variant="p" color="inherit">
								No queued messages match the current criteria.
							</Box>
						</Box>
					}
				/>
			</>
		);
	}

	render() {
		return (
			<Tabs
				activeTabId={this.state.activeTabId}
				onChange={({ detail }) => {
					this.setState({ activeTabId: detail.activeTabId }, () => {
						if (detail.activeTabId === "queue" && !this.props.notifications.queue.loaded) {
							this.props.Interface.loadNotificationQueue(this.state.queueSearch, null, this.state.queueDateRange);
						} else if (detail.activeTabId === "tags" && !this.props.notificationTags.loaded) {
							this.props.Interface.loadNotificationTags().catch(() => {});
						}
					});
				}}
				tabs={[
					{
						id: "notifications",
						label: "Notifications",
						content: this.renderNotificationsTable(),
					},
					{
						id: "tags",
						label: "Notification tags",
						content: this.renderNotificationTagsTable(),
					},
					{
						id: "queue",
						label: "Queue",
						content: this.renderQueueTable(),
					},
				]}
			/>
		);
	}
}

function withRecoil(ComponentToWrap) {
	return function WrappedComponent(props) {
		const notifications = useRecoilValue(notificationsStore);
		const setNotifications = useSetRecoilState(notificationsStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const notificationsAddModal = useRecoilValue(notificationsAddModalStore);
		const setNotificationsAddModal = useSetRecoilState(notificationsAddModalStore);
		const notificationsEditModal = useRecoilValue(notificationsEditModalStore);
		const setNotificationsEditModal = useSetRecoilState(notificationsEditModalStore);
		const notificationsStatsModal = useRecoilValue(notificationsStatsModalStore);
		const setNotificationsStatsModal = useSetRecoilState(notificationsStatsModalStore);
		const setNotificationsRepeatModal = useSetRecoilState(notificationsRepeatModalStore);
		const notificationTags = useRecoilValue(notificationTagsStore);
		const setNotificationTags = useSetRecoilState(notificationTagsStore);

		return (
			<ComponentToWrap
				{...props}
				notifications={notifications}
				setNotifications={setNotifications}
				config={config}
				setConfig={setConfig}
				notificationsAddModal={notificationsAddModal}
				setNotificationsAddModal={setNotificationsAddModal}
				notificationsEditModal={notificationsEditModal}
				setNotificationsEditModal={setNotificationsEditModal}
				notificationsStatsModal={notificationsStatsModal}
				setNotificationsStatsModal={setNotificationsStatsModal}
				setNotificationsRepeatModal={setNotificationsRepeatModal}
				notificationTags={notificationTags}
				setNotificationTags={setNotificationTags}
			/>
		);
	};
}

export default withRecoil(Notifications);
